// What waits on the user on a plan board (shared/plan.ts needsYou), counted wherever the board's name shows:
// the board pill, the boards list, a phone's boards sheet and the tab's title. Small and eager, since those
// load before any plan does.

import { needsYou } from "../../../shared/plan";
import { shownBlocked } from "../../state/notices";
import { kindOf } from "../../../shared/boardKinds";
import type { ID } from "../../../shared/types";
import { boardEntries, useStore } from "../../state/store";

/** How many things wait on the user on a board: questions to answer, blocked steps (not one they said is still waiting), and the session building it when it waits on them. 0 for any other kind. */
export function usePlanWaiting(boardId: ID | null | undefined): number {
  return useStore((s) => {
    const b = boardId ? s.boards.find((x) => x.id === boardId) : null;
    if (!b || kindOf(b) !== "plan") return 0;
    const session = s.runner?.sessionsOn && (s.runner.sessions ?? []).some((x) => x.plan === b.id && x.state === "needs_you");
    const entries = boardEntries(s, b.id);
    return needsYou(entries).questions.length + shownBlocked(s.workspace?.dismissed, entries).length + (session ? 1 : 0);
  });
}

/** The count, as a small amber badge beside the board's name, or nothing. */
export function PlanWaiting({ boardId }: { boardId: ID }) {
  const n = usePlanWaiting(boardId);
  if (!n) return null;
  return (
    <span className="plan-waiting" aria-label={`${n} waiting on you`} title={`${n} waiting on you`}>
      {n}
    </span>
  );
}
